import { Button } from "@gigpro/ui/Button";
import { DialogFooter } from "@gigpro/ui/Dialog";
import { FormControl, FormField, FormItem, FormLabel, FormMessage, useFormContext } from "@gigpro/ui/Form";
import { DateField } from "@gigpro/ui/Form/DateField";
import { Textfield } from "@gigpro/ui/Form/Textfield";
import { TimeField } from "@gigpro/ui/Form/TimeField";
import { Skeleton } from "@gigpro/ui/Skeleton";
import { ToggleGroup, ToggleGroupItem } from "@gigpro/ui/ToggleGroup";
import { formatDate } from "@gigpro/utils/datetime";
import type { ConvertibleShift, WorkingInterviewSeriesRequestRequest } from "@sidegigllc/api/client";
import { WEEKDAYS } from "domains/hire/components/AvailabilityWindowsField";
import { ConvertibleShiftList } from "domains/hire/components/ConvertibleShiftList";
import type { ShiftConversion } from "domains/hire/components/ConvertibleShiftList/seats";
import type React from "react";
import { useWatch } from "react-hook-form";
import { type SeriesValues, seriesSummary } from "./series";

/** What the business chose on the step: shown on Review and posted once the job exists. */
export type WorkingInterviewChoice = { summary: string } & (
  | { kind: "series"; request: WorkingInterviewSeriesRequestRequest }
  /** Posted shifts whose open seats become the job's working interviews (decision 2). */
  | { kind: "convert"; conversions: Array<ShiftConversion> }
);

export interface SeriesPreviewState {
  /** The server's count for the current values; absent while it loads or while the values are incomplete. */
  data?: { count: number; until: Date };
  isLoading: boolean;
  /** The server refused the values, e.g. no day falls between the start and end date. */
  error?: string;
}

const SeriesPreviewLine = ({ preview }: { preview: SeriesPreviewState }) => {
  const { control } = useFormContext<SeriesValues>();
  const [weekdays, seats] = useWatch({ control, name: ["weekdays", "seats"] });
  if (preview.error) return <p className="text-small font-semibold text-error">{preview.error}</p>;
  if (preview.isLoading) return <Skeleton className="h-5 w-3/4" />;
  if (!preview.data) return null;
  return (
    <output className="block text-normal font-semibold text-black">
      {seriesSummary({ weekdays, seats }, preview.data, (date) => formatDate(date, "MMM d"))}
    </output>
  );
};

const DaysField = () => {
  const { control } = useFormContext<SeriesValues>();
  return (
    <FormField
      control={control}
      name="weekdays"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Days</FormLabel>
          <FormControl>
            <ToggleGroup
              type="multiple"
              value={field.value.map(String)}
              // Sorted, not press order, so the summary reads the week in order.
              onValueChange={(days) => field.onChange(days.map(Number).toSorted((a, b) => a - b))}
              className="gap-1 bg-transparent p-0"
            >
              {WEEKDAYS.map((day) => (
                <ToggleGroupItem
                  key={day.name}
                  value={String(day.index)}
                  aria-label={day.name}
                  // `p-0` so padding cannot fight `size-8` and inflate the circle.
                  className="size-8 shrink-0 items-center justify-center rounded-full bg-light-gray p-0 text-brand"
                >
                  {day.initial}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

const SeriesFields = () => {
  const { control } = useFormContext<SeriesValues>();
  return (
    <>
      <DaysField />
      <fieldset className="grid grid-cols-2 gap-4">
        {/* Wall-clock times at the location, so a browser-timezone caption would be wrong. */}
        {/* The start date's "in the future" check reads the start time too. */}
        <TimeField control={control} name="startTime" label="Start time" hideTimezone deps="startDate" />
        <TimeField control={control} name="endTime" label="End time" hideTimezone />
      </fieldset>
      {/* Its own row: a text input is taller than the time pickers and would misalign them. */}
      <Textfield
        control={control}
        name="seats"
        label="People per shift"
        type="number"
        inputMode="numeric"
        min={1}
        placeholder="2"
        transform={{ input: String, output: Number }}
        className="w-40"
      />
      <fieldset className="grid grid-cols-2 gap-4">
        <DateField control={control} name="startDate" label="Starting" />
        <DateField control={control} name="until" label="Ending" />
      </fieldset>
    </>
  );
};

interface StepFrameProps {
  children: React.ReactNode;
  onBack: () => void;
  onSkip: () => void;
  /** Absent while loading. */
  onContinue?: () => void;
}

const StepFrame = ({ children, onBack, onSkip, onContinue }: StepFrameProps) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-1">
      <p className="text-large font-semibold">Add working interviews</p>
      <p className="text-small text-gray">
        Candidates try out on a real shift. Qwick invites the best fits and books them for you.
      </p>
    </div>
    {children}
    <Button variant="text" className="self-center" onClick={onSkip}>
      Skip, I'll schedule interviews myself
    </Button>
    <DialogFooter className="grid grid-cols-2 gap-2">
      <Button variant="text" onClick={onBack}>
        Back
      </Button>
      <Button variant="contained" onClick={onContinue} disabled={!onContinue}>
        Continue
      </Button>
    </DialogFooter>
  </div>
);

/** While the suggested pattern loads, or when the step can't load; Skip and Back still work. */
interface WorkingInterviewStepLoadingProps extends Omit<StepFrameProps, "children" | "onContinue"> {
  /** Shown instead of the skeleton when the step can't load at all. */
  error?: string;
}

export const WorkingInterviewStepLoading = ({ onBack, onSkip, error }: WorkingInterviewStepLoadingProps) => (
  <StepFrame onBack={onBack} onSkip={onSkip}>
    {error ? (
      <p role="alert" className="text-normal text-gray">
        {error}
      </p>
    ) : (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading your shift history">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    )}
  </StepFrame>
);

const BranchSwitch = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button variant="text" className="self-start px-0" onClick={onClick}>
    {label}
  </Button>
);

export interface WorkingInterviewStepProps {
  /** Shifts the suggested pattern was read from; 0 when the location has no history and the picker starts blank. */
  patternSampleSize: number;
  preview: SeriesPreviewState;
  onBack: () => void;
  onSkip: () => void;
  onContinue: () => void;
  /** Back to the location's posted shifts; absent when it has none to convert. */
  onUseMyShifts?: () => void;
}

/**
 * The posting flow's working-interview step (decisions 1 and 4): a weekly schedule pre-filled from the location's
 * shift history, otherwise a blank picker, with the server's count of what will post. On by default, with one way
 * out. The fields read the surrounding `FormProvider`.
 */
export const WorkingInterviewStep = ({
  patternSampleSize,
  preview,
  onBack,
  onSkip,
  onContinue,
  onUseMyShifts,
}: WorkingInterviewStepProps) => (
  <StepFrame onBack={onBack} onSkip={onSkip} onContinue={onContinue}>
    <p className="text-small text-gray">
      {patternSampleSize > 0
        ? "We filled this in from the shifts you've posted for this role over the last 8 weeks."
        : "Pick the days and times you'd like candidates to work a shift."}
    </p>
    <SeriesFields />
    <SeriesPreviewLine preview={preview} />
    {onUseMyShifts && <BranchSwitch label="Use my posted shifts instead" onClick={onUseMyShifts} />}
  </StepFrame>
);

export interface WorkingInterviewConvertStepProps {
  shifts: Array<ConvertibleShift>;
  /** Location zone the shifts are shown in. */
  timezone: string;
  /** Picked gig ids, as strings. */
  selected: Array<string>;
  onSelectedChange: (selected: Array<string>) => void;
  /** Seats chosen per gig id. */
  seats: Record<number, number>;
  onSeatsChange: (gigId: number, seats: number) => void;
  onBack: () => void;
  onSkip: () => void;
  /** Absent while nothing is picked. */
  onContinue?: () => void;
  onScheduleNew: () => void;
}

/** Decision 2: shifts the location already posted come before a new schedule. */
export const WorkingInterviewConvertStep = ({
  shifts,
  timezone,
  selected,
  onSelectedChange,
  seats,
  onSeatsChange,
  onBack,
  onSkip,
  onContinue,
  onScheduleNew,
}: WorkingInterviewConvertStepProps) => (
  <StepFrame onBack={onBack} onSkip={onSkip} onContinue={onContinue}>
    <p className="text-small text-gray">
      You already posted these shifts for this role. Qwick invites the best candidates for this job to work their open
      seats. Pros already confirmed stay booked, and open offers on these seats are withdrawn.
    </p>
    <ConvertibleShiftList
      shifts={shifts}
      timezone={timezone}
      value={selected}
      onValueChange={onSelectedChange}
      seats={seats}
      onSeatsChange={onSeatsChange}
    />
    <BranchSwitch label="Set up new shifts instead" onClick={onScheduleNew} />
  </StepFrame>
);
